/**
 * The Zod-free runtime values browser code may import (`@kopi/contracts/client`): limits and small
 * helpers. Importing the schema modules instead would put Zod in every route's initial bundle (PRD §5
 * budget). The schemas import these too, so both sides share one value.
 */

export {
  DISCOUNT_APPROVAL_STATUSES,
  DISCOUNT_ERROR_CODES,
  DISCOUNT_KINDS,
  DISCOUNT_SCOPES,
  type DiscountErrorCode,
  type DiscountKind,
  type DiscountScope,
} from "./codes";
export * from "./images";
export * from "./sorts";

export const MAX_DISCOUNT_OUTLETS = 100;
export const MAX_DISCOUNT_BASIS_POINTS = 10_000;
export const DISCOUNT_APPROVAL_TTL_MS = 15 * 60 * 1000;

/** Most options one modifier group holds, and most groups one product offers. */
export const MAX_MODIFIER_OPTIONS = 30;
export const MAX_PRODUCT_MODIFIER_GROUPS = 20;

/** The longest range one sales report or export covers (R1-43): about a quarter, so a request stays bounded. */
export const MAX_REPORT_DAYS = 92;

/** The largest push accepted in one request; clients retry the rest after a partial response. */
export const MAX_SYNC_BATCH_SIZE = 100;
/** The largest page of changes one pull returns; the client keeps pulling while `hasMore`. */
export const MAX_SYNC_PULL_LIMIT = 100;
/** The command schema this build writes. */
export const SYNC_SCHEMA_VERSION = 1;
/**
 * The oldest command schema the server still ingests (AC-22): sales queued by a register running an
 * older build sync after it updates. Raise it only once no supported client can still hold one.
 */
export const MIN_SYNC_SCHEMA_VERSION = 1;

/** The most rows a report breakdown shows, by net sales; the product export has them all. */
export const MAX_REPORT_ROWS = 100;

/** The most low-stock items the owner's dashboard lists; it states how many there are in all. */
export const MAX_DASHBOARD_LOW_STOCK = 5;
/** Best sellers the owner's dashboard lists (R1-44). */
export const MAX_DASHBOARD_TOP_PRODUCTS = 5;

/** How many business days `from`..`to` (`YYYY-MM-DD`) covers, both included. */
export function reportDays(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1;
}

/** Most lines one recipe version holds, base lines and modifier deltas together. */
export const MAX_RECIPE_LINES = 60;

/** Most cash movements one shift holds (its detail lists them all). */
export const MAX_SHIFT_MOVEMENTS = 100;

/** Most distinct lines one order holds (R1-16); larger orders split into separate bills. */
export const MAX_ORDER_LINES = 50;

/** Longest customer name, and longest phone/email as typed (before normalisation). */
export const MAX_CUSTOMER_NAME = 80;
export const MAX_CUSTOMER_PHONE = 32;
export const MAX_CUSTOMER_EMAIL = 254;

/** Longest table label a dine-in order carries ("12", "Teras 3", "VIP-A"). */
export const MAX_TABLE_LABEL = 16;

/** Most lines one stock receipt or count holds (R1-23). */
export const MAX_STOCK_DOCUMENT_LINES = 100;

/** Most rupiah a drawer amount (float, movement, tender) can be: Rp 1.000.000.000. */
export const MAX_DRAWER_AMOUNT = 1_000_000_000n;

/**
 * The filters actually set: drops undefined, null and blank-string values, so a list query or
 * repository filter carries only what narrows it (a blank `q` means no search).
 */
export function definedOnly<T extends Record<string, unknown>>(
  values: T,
): { [K in keyof T]?: Exclude<T[K], undefined | null | ""> } {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  ) as { [K in keyof T]?: Exclude<T[K], undefined | null | ""> };
}
